import { useCallback, useEffect, useMemo, useState } from 'react';

import { CategoryButtons } from '#components/organisms/Store/CategoryButtons/CategoryButtons.js';
import { useShopProvider } from '#provider/useShopProvider.js';
import {
  StoreItemSearch,
  StoreItemWithOptionalInventorySummary,
  useInfiniteListStoreItems,
  useShowStoreCause,
  useStoreItemCount,
} from '@givebacks/api/store';
import { useBreakPoints } from 'core';
import { centsToDollar } from 'core/Utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  ButtonMantine,
  Center,
  Collapse,
  Container,
  DEFAULT_IMAGE_PLACEHOLDER,
  DEFAULT_PTA_IMAGE,
  Group,
  Loader,
  NoResults,
  ProductCard,
  SearchInput,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from 'ui';
import { useDebouncedValue, useInViewport } from 'ui/Hooks';

import { StoreHeader } from '../../../components/organisms/Store/StoreHeader';
import { FilterProducts } from '../../../components/organisms/modals/FilterProducts/FilterProducts';
import { useRouteStore } from '../../../hooks/useRouteStore';
import { StoreSearchProvider, useStoreSearchContext } from './StoreSearchContext';

const baseStoreSearch: StoreItemSearch = {
  order: {
    field: 'order',
    direction: 'asc',
  },
  searches: {
    type: {
      value: 'StoreItems::Item,StoreItems::Membership',
      by: 'in',
    },
    in_store: {
      value: true,
    },
    live: {
      value: true,
    },
  },
  options: {
    include: 'inventory_summary',
  },
  join: 'AND',
};

const StoreItemCard = ({
  item,
  isPtaMembership,
}: {
  item: StoreItemWithOptionalInventorySummary;
  isPtaMembership?: boolean;
}) => {
  const navigate = useNavigate();

  const productImage = useMemo(() => {
    if (isPtaMembership && !item.images[0]) {
      return DEFAULT_PTA_IMAGE;
    } else {
      return item.images[0]?.url ?? DEFAULT_IMAGE_PLACEHOLDER;
    }
  }, [isPtaMembership, item.images]);

  const isOutOfStock = useMemo(() => {
    if (!item.manage_inventory) return false;

    if (item.inventory_summary?.amount === 0) {
      return true;
    }

    return false;
  }, [item.manage_inventory, item.inventory_summary?.amount]);

  return (
    <ProductCard
      key={item.uuid}
      img={productImage}
      title={item.name}
      description={item.content?.short_description}
      price={centsToDollar(item.price)}
      isOutOfStock={isOutOfStock}
      onClick={() => void navigate(`items/${item.uuid}`)}
    />
  );
};

const StoreItems = () => {
  const { ref, inViewport } = useInViewport();
  const ctx = useStoreSearchContext();
  const { useShowStore, storeId } = useRouteStore();
  const { data: cause } = useShowStore();

  const { data, hasNextPage, fetchNextPage, isError, isFetchingNextPage, isLoading } =
    useInfiniteListStoreItems({
      causeId: storeId,
      queryOptions: ctx.storeSearchObject,
    });

  const hasResults = data && data?.pages[0]?.store_items?.length > 0;

  useEffect(() => {
    if (inViewport && hasNextPage) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Fetching next page is fire-and-forget
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, inViewport]);

  const isPta = useMemo(() => cause?.in_state_pta, [cause?.in_state_pta]);

  return (
    <>
      {isLoading && (
        <Center w="100%" h={250}>
          <Loader />
        </Center>
      )}
      {hasResults ? (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm" verticalSpacing="md">
          {data?.pages.map((page) => {
            return page.store_items.map((item) => (
              <StoreItemCard
                key={item.uuid}
                isPtaMembership={isPta && item.type === 'StoreItems::Membership'}
                item={item}
              />
            ));
          })}
        </SimpleGrid>
      ) : (
        <NoResults isLoading={isLoading} />
      )}

      <Center ref={ref} py="xl">
        {isFetchingNextPage && (
          <Group>
            <Loader />
            <Text fz="lg" fw={500} c="gray.2">
              Loading...
            </Text>
          </Group>
        )}
        {isError && (
          <Stack gap="xs">
            <Text c="gray" ta="center">
              Failed to Load
            </Text>
            <ButtonMantine size="md" variant="gray" onClick={() => void fetchNextPage()}>
              Reload Products
            </ButtonMantine>
          </Stack>
        )}
      </Center>
    </>
  );
};

const EmptyStoreContent = () => {
  return (
    <Center>
      <Stack gap="xs" ta="center" maw={272}>
        <Text fz="xl" fw={600} c="gray">
          This store isn&apos;t available.
        </Text>
        <Text fz="xs" c="gray">
          If you&apos;re an administrator, please check your store settings to complete setup.
        </Text>
      </Stack>
    </Center>
  );
};

const SearchStoreItemByName = () => {
  const { storeSearchObject, setStoreSearchObject } = useStoreSearchContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

  const setSearchQuery = useCallback(
    (value: string) => {
      const nextParams = new URLSearchParams(searchParams);

      if (value && value.trim().length > 0) {
        nextParams.set('search', value);
        nextParams.delete('category');
      } else {
        nextParams.delete('search');
      }

      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (debouncedSearchQuery && debouncedSearchQuery.trim().length > 0) {
      setStoreSearchObject({
        searches: {
          ...storeSearchObject?.searches,
          name: { value: debouncedSearchQuery },
          'categories.uuid': { value: undefined },
          type: baseStoreSearch.searches?.type,
        },
      });
    } else {
      setStoreSearchObject({
        searches: {
          ...storeSearchObject?.searches,
          name: { value: undefined },
        },
      });
    }
  }, [debouncedSearchQuery, setStoreSearchObject]);

  return (
    <SearchInput
      placeholder="Search products"
      value={searchQuery}
      onChange={(event: { target: { value: string } }): void => {
        setSearchQuery(event.target.value);
      }}
    />
  );
};

const Store = () => {
  const { storeFrontOptions } = useShopProvider();
  const {
    showFilter,
    banner: StorefrontBanner,
    showTitle = true,
    showBanner = true,
    showCategoryButtons,
    storePageContainerProps,
  } = storeFrontOptions ?? {};
  const { isSmallScreen } = useBreakPoints();
  const { storeId } = useRouteStore() as { storeId: string };
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');

  const [storeSearchObject, setStoreSearchObject] = useState<StoreItemSearch>(baseStoreSearch);

  const _setStoreSearchObject = useCallback((value: StoreItemSearch) => {
    setStoreSearchObject({ ...baseStoreSearch, ...value });
  }, []);

  const { data: cause } = useShowStoreCause({ causeId: storeId });

  const { data: storeItemsCount, isLoading } = useStoreItemCount({
    causeId: storeId,
    queryOptions: baseStoreSearch,
  });

  useEffect(() => {
    if (category === 'memberships') {
      _setStoreSearchObject({
        searches: {
          ...storeSearchObject?.searches,
          'categories.uuid': { value: undefined },
          type: {
            value: 'StoreItems::Membership',
          },
        },
      });
    } else if (category) {
      _setStoreSearchObject({
        searches: {
          ...storeSearchObject?.searches,
          'categories.uuid': { value: category },
          type: baseStoreSearch.searches?.type,
        },
      });
    } else {
      _setStoreSearchObject({
        searches: {
          ...storeSearchObject?.searches,
          'categories.uuid': { value: undefined },
          type: baseStoreSearch.searches?.type,
        },
      });
    }
  }, [category, _setStoreSearchObject]);

  const header = useMemo(() => {
    if (!showBanner) {
      return null;
    }

    if (StorefrontBanner) {
      return <StorefrontBanner />;
    }

    if (!cause) {
      return null;
    }

    return <StoreHeader uuid={cause.uuid} productCount={storeItemsCount} />;
  }, [cause, storeItemsCount, StorefrontBanner, showBanner]);

  if (!cause) return null;

  return (
    <StoreSearchProvider
      value={{
        storeSearchObject,
        setStoreSearchObject: _setStoreSearchObject,
      }}
    >
      <Container pt="sm" pb="lg" px={isSmallScreen ? 'xs' : 'md'} {...storePageContainerProps}>
        <Stack gap={isSmallScreen ? 'lg' : 'xl'}>
          {header}

          {!isLoading && (!storeItemsCount || !cause.payments_enabled) ? (
            <EmptyStoreContent />
          ) : (
            <>
              <Group gap="sm">
                {showTitle && (
                  <Title order={2} style={{ order: 1, flexGrow: 1 }}>
                    All Products
                  </Title>
                )}
                <Box
                  style={{ order: isSmallScreen ? 3 : 2 }}
                  miw={{ base: '100%', sm: 345 }}
                  flex={showTitle ? undefined : 1}
                >
                  <SearchStoreItemByName />
                </Box>

                {showFilter && (
                  <Box style={{ order: isSmallScreen ? 2 : 3 }}>
                    <FilterProducts causeId={cause.uuid} />
                  </Box>
                )}
              </Group>

              <Stack>
                <Collapse in={!storeSearchObject?.searches?.name?.value && !!showCategoryButtons}>
                  <CategoryButtons storeId={storeId} />
                </Collapse>
                <StoreItems />
              </Stack>
            </>
          )}
        </Stack>
      </Container>
    </StoreSearchProvider>
  );
};

export default Store;
